import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import {
  useCreateIngestJobMutation,
  useUpdateIngestJobUploadCompletedMutation,
} from '../../services/ingest.service';
import { NgButton } from '../UI';
import { buttonVariants } from '@clatter/ui';
import { CircularLoader } from '../../shared';

interface FileUploadProps {
  title?: string;
  targetKey?: string;
  disabled?:boolean;
  targetId?: number | null;
  isLoading?: boolean;
  acceptedFileTypes?: string;
  onChange?: (data: any) => void;
  onError?: (data: any) => void;
  onUpload?: (data: any) => void;
}

const StyledUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 30px;
  min-height: 240px;
  gap: 20px;

  border: 2px solid lightgrey;
  border-radius: ${({ theme }) => theme.radius.small};
  text-align: center;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    border: 2px solid lightskyblue;
  }

  &.drag-over {
    border: 2px dashed lightskyblue;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  button {
    cursor: pointer;
  }
`;

export const NgFileUpload: React.FC<FileUploadProps> = ({
  targetId,
  targetKey = 'project_id',
  title = '',
  acceptedFileTypes = '.xlsx, .xls, .csv',
  disabled,
  onChange,
  onUpload,
  onError,
  isLoading: isParentLoading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);

  const [createIngestJob] = useCreateIngestJobMutation();
  const [updateIngestJobUploadCompleted] = useUpdateIngestJobUploadCompletedMutation();

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(true);
  };

  const handleUploadFile = async (file: any) => {
    if (typeof onUpload === 'function') {
      onUpload(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return;
    }

    try {
      // 1. Create ingest job and get [resigned url and ingest id
      console.info('creating ingest job for file upload');
      // const ingestJobResponse = await createIngestJob({ fileName: file.name, projectId: targetId }).unwrap();
      const payload = { fileName: file.name, [targetKey]: targetId }
      const ingestJobResponse = await createIngestJob(payload).unwrap();

      console.info('creating ingest job succeeded');

      if (!ingestJobResponse?.data?.id) {
        console.error('Unable to upload file. No ingest-job id provided!');
        return;
      }

      // 2. Upload file to presigned url
      console.info('starting file upload');

      if (!ingestJobResponse?.data?.upload_location) {
        console.error('Unable to upload file. No upload url provided!');
        return;
      }

      setIsUploadLoading(true);

      await fetch(ingestJobResponse.data.upload_location, { method: 'PUT', body: file });

      await updateIngestJobUploadCompleted({ ingestJobId: ingestJobResponse.data.id });

      setIsUploadLoading(false);

      // Reset the input value to allow to re-upload of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // 3. emit event with ingestId so we could start polling
      if (typeof onChange === 'function') {
        onChange({ ingestJobId: ingestJobResponse.data.id });
      }
    } catch (error: any) {
      setIsUploadLoading(false);
      console.error('Upload failed');

      // Reset the input value to allow to re-upload of the same file
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (typeof onError === 'function') {
        onError({ error, file });
      }
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      await handleUploadFile(file);
    }
  };

  const handleFileDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.items) {
      const file = event.dataTransfer.items[0].getAsFile();
      if (file) {
        await handleUploadFile(file);
      }
    }
    setIsDraggingOver(false);
  };

  return (
    <StyledUploadContainer
      className={classNames({ 'drag-over': isDraggingOver, 'disabled': isUploadLoading || isParentLoading || disabled })}
      onDrop={(e) => handleFileDrop(e)}
      onDragOver={(e) => handleDragOver(e)}
      onClick={() => handleUploadClick()}
    >
      <p>{title}</p>
      <input
        type="file"
        disabled={isDraggingOver || isUploadLoading || isParentLoading || disabled}
        accept={acceptedFileTypes}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {isUploadLoading ? (
        <CircularLoader />
      ) : (
        <NgButton type="button" variant={buttonVariants.primary} disabled={isDraggingOver || isUploadLoading || isParentLoading || disabled}>
          Upload File
        </NgButton>
      )}

      <p>Or drag a file here</p>
    </StyledUploadContainer>
  );
};
